import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { HANDLE_ERROR_AND_THROW } from '@helios/utils'
import { Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { derivedScalar } from '../edit-transaction/derivation'
import { ListRecord } from '../models/transaction-metadata.model'
import {
  DerivationRequest,
  DerivationResult,
} from './transaction-derivation.service'
import { TransactionMetadataSource } from './transaction-metadata.service'

/**
 * The flex-transactions pass-through added by helios PVAL-26442 (!510), which relays RPS's
 * `POST /api/v1/transaction-dialog-metadata/resolve` (PVAL-26563).
 *
 * With `<base href="/helios/">` this resolves to
 * `/helios/helios-flex-transactions/transactions/dialog-metadata/resolve` — the same host, and so
 * the same LOCAL-ONLY proxy entry, as the metadata read beside it.
 */
export const DIALOG_METADATA_RESOLVE_URL = `helios-flex-transactions/transactions/dialog-metadata/resolve`

/**
 * `params.accountId`: the account the dialog is open on, carried by EVERY item of every resolve call
 * (helios PVAL-26686 / PVAL-26693).
 *
 * helios requires it on each item and requires them to agree, checks it against the caller's session
 * (a 400 `accountNotInSession` otherwise), forwards it to RPS unchanged and derives the `clientId`
 * from it itself. There is no `?accountId=` query and no item carries a `clientId`: the browser never
 * asserts whose data it is reading.
 */
export const ACCOUNT_ID_KEY = 'accountId'

/**
 * The key a search list's typed text goes under (PVAL-26685).
 *
 * RPS defines no request key: each lookup's stored url and body name theirs, and the document serves
 * the names as `requestKeys`. The dialog sends the ones it holds a value for and leaves the rest
 * out; the typed text is the one value it supplies per item, under this name.
 */
export const SEARCH_TEXT_KEY = 'searchText'

/**
 * One item of the resolve request. `type` is the serving tier's own spelling: `List` reads the
 * row's `list_values`, `Derivation` its `derivation_parameters`.
 */
export interface ResolveRequestItem {
  source: string
  keyName: string
  type: 'List' | 'Derivation'
  /**
   * Keyed by the names the stored spec's url and body carry: a list's served `requestKeys`, a
   * derivation's inputs. RPS fills them into the upstream request. `post` adds `accountId` to every
   * item before it is sent, so a caller leaves it out.
   */
  params?: Record<string, string>
}

/** One item of the reply. Exactly one of `value` | `error` is present. */
interface ResolveResultItem {
  source?: string
  keyName?: string
  type?: string
  value?: unknown
  error?: unknown
}

/** What a resolve pass produced: the rows that arrived, and whatever did not. */
export interface ResolvedLists {
  /**
   * Keyed by field name: the ROWS `/resolve` answered, as the upstream spelled them. Read through
   * the field's `displayColumns` / `displayText` / `valueKey` (see `normalizeOptions`). Only fields
   * that resolved to at least one row appear.
   */
  options: Map<string, ListRecord[]>
  /** Per-key failures from the 200 body — a missing row, or an upstream that refused. */
  failures: Map<string, unknown>
  /** The whole call failed. Per-key failures are in {@link failures}, not here. */
  error: unknown | null
}

/** A mixed reply: the lists, and one result per `Derivation` item asked for. */
export interface ResolvedReply extends ResolvedLists {
  derivations: DerivationResult[]
}

const empty = (): ResolvedReply => ({
  options: new Map(),
  failures: new Map(),
  error: null,
  derivations: [],
})

/**
 * The ONE transport to `dialog-metadata/resolve` (PVAL-26443, PVAL-26685).
 *
 * Lists and derivations travel through it alike: a `catalog` list as a `List` item, a typeahead as
 * a `List` item with `searchText`, a derivation as a `Derivation` item with its inputs' values.
 * The dialog's opening call mixes the first and the last, so one POST fills every catalog and every
 * `context` derivation at once.
 *
 * **Replies are projected by RPS** (PVAL-26675): a list answers `value: {options: [row…]}`, each
 * row carrying the field's display columns and its value key; a derivation answers its scalar. A
 * bare root array is still read as rows, to tolerate an RPS that predates the projection, and a
 * derivation's raw reply is read through the field's served `derivationDisplayText`. That happens
 * here, in the one place every `/resolve` reply is read, so the dialog's opening call and the
 * field-triggered derivations get the same scalar.
 */
@Injectable()
export class HttpDialogListValuesService {
  constructor(
    private readonly http: HttpClient,
    private readonly metadata: TransactionMetadataSource,
  ) {}

  /**
   * Resolve every list the document still owes rows for, together with the dialog's opening
   * derivations, in one call.
   *
   * Answers without calling when there is nothing outstanding and nothing to derive — which is the
   * steady state for lists after the first dialog, since the rows are held on the metadata source.
   */
  resolveOutstanding(
    accountId: string,
    derivations: readonly DerivationRequest[] = [],
  ): Observable<ResolvedReply> {
    const keys = this.metadata.unresolvedListKeys()
    if (keys.length === 0 && derivations.length === 0) return of(empty())
    return this.post(
      [...this.listItems(keys), ...this.derivationItems(derivations)],
      accountId,
    )
  }

  /**
   * One `search` field, against what the user has typed.
   *
   * `params` holds the list's served `requestKeys` the dialog can fill: the typed text under
   * `searchText`, which the stored url's `{searchText}` token names and RPS fills in the upstream URL
   * (PVAL-26674). A document that predates `requestKeys` names none, and a typeahead then still sends
   * its text. `post` adds the account.
   */
  search(
    keyName: string,
    query: string,
    accountId: string,
  ): Observable<ResolvedLists> {
    const served = this.metadata.requestKeysOf(keyName)
    return this.post(
      [
        this.listItem(
          keyName,
          { [SEARCH_TEXT_KEY]: query },
          served.length ? served : [SEARCH_TEXT_KEY],
        ),
      ],
      accountId,
    )
  }

  /** Resolve a named set of keys. Exposed for the one caller that knows better than the document. */
  resolve(
    keyNames: readonly string[],
    accountId: string,
  ): Observable<ResolvedLists> {
    if (keyNames.length === 0) return of(empty())
    return this.post(this.listItems(keyNames), accountId)
  }

  /**
   * Post any mix of items for one account.
   *
   * Every item leaves carrying `params.accountId`, derivations included: helios requires the same
   * one on all of them (PVAL-26693), so it is stamped here once rather than by each caller. The
   * dialog's own account wins over any an item arrived with, which is what keeps them agreeing.
   *
   * Never errors: a failed call answers `error`, and every `Derivation` item asked for then carries
   * that error too, so a caller waiting on one field's derivation is told rather than left waiting.
   */
  post(
    items: readonly ResolveRequestItem[],
    accountId: string,
  ): Observable<ResolvedReply> {
    const asked = items.map(item => ({
      ...item,
      params: { ...item.params, [ACCOUNT_ID_KEY]: accountId },
    }))
    return this.http
      .post<unknown>(DIALOG_METADATA_RESOLVE_URL, asked, {
        // Without this the app's global `ErrorInterceptor` answers `EMPTY` on every HTTP error:
        // the stream completes WITHOUT EMITTING, so neither `catchError` nor `next` runs and a
        // caller waiting on the result waits forever. Same opt-in the trade read uses.
        context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
      })
      .pipe(
        map(document => this.readResult(document, asked)),
        catchError((error: unknown) => of(this.failedCall(asked, error))),
      )
  }

  /** One `List` item per key. A catalog supplies nothing of its own; `post` adds the account. */
  private listItems(keyNames: readonly string[]): ResolveRequestItem[] {
    return keyNames.map(keyName => this.listItem(keyName, {}))
  }

  /**
   * One `List` item, its `params` being the list's request keys that `supplied` holds a value for.
   * A list that takes none — the enfusion catalogs — carries no `params` of its own.
   */
  private listItem(
    keyName: string,
    supplied: Readonly<Record<string, string>>,
    requestKeys: readonly string[] = this.metadata.requestKeysOf(keyName),
  ): ResolveRequestItem {
    const params: Record<string, string> = {}
    for (const key of requestKeys) {
      if (supplied[key] !== undefined) params[key] = supplied[key]
    }
    return {
      source: this.metadata.source,
      keyName,
      type: 'List',
      ...(Object.keys(params).length ? { params } : {}),
    }
  }

  private derivationItems(
    derivations: readonly DerivationRequest[],
  ): ResolveRequestItem[] {
    return derivations.map(item => ({
      source: this.metadata.source,
      keyName: item.keyName,
      type: 'Derivation',
      params: item.params,
    }))
  }

  private failedCall(
    asked: readonly ResolveRequestItem[],
    error: unknown,
  ): ResolvedReply {
    return {
      ...empty(),
      error,
      derivations: asked
        .filter(item => item.type === 'Derivation')
        .map(item => ({ keyName: item.keyName, error })),
    }
  }

  /**
   * The 200 body, split into what resolved and what did not.
   *
   * A per-key `error` stays a per-key fact: the call succeeded, and one unresolvable catalog must
   * not blank the eleven that resolved. Only a non-conforming body — no `results` array at all,
   * which is what a proxy fall-through to `index.html` looks like — is a whole-call failure.
   */
  private readResult(
    document: unknown,
    asked: readonly ResolveRequestItem[],
  ): ResolvedReply {
    const results = (document as { results?: unknown })?.results
    if (!Array.isArray(results)) {
      return this.failedCall(asked, {
        message:
          'dialog-metadata/resolve did not answer a `results` array; the request may not have ' +
          'reached flex-transactions',
        requested: asked.map(item => item.keyName),
        body: document,
      })
    }

    const listKeys = new Set(
      asked.filter(item => item.type === 'List').map(item => item.keyName),
    )
    const derivationKeys = new Set(
      asked
        .filter(item => item.type === 'Derivation')
        .map(item => item.keyName),
    )

    const reply = empty()
    const answered = new Set<string>()
    for (const entry of results as ResolveResultItem[]) {
      // Matched by `keyName` and `type`, never by position: nothing promises the reply is ordered
      // like the request, and `tradeCurrency` is both a list and a derivation.
      const key = typeof entry?.keyName === 'string' ? entry.keyName : ''
      if (!key) continue
      const type =
        entry.type === 'List' || entry.type === 'Derivation'
          ? entry.type
          : derivationKeys.has(key) && !listKeys.has(key)
          ? 'Derivation'
          : 'List'

      if (type === 'Derivation') {
        if (!derivationKeys.has(key)) continue
        answered.add(key)
        reply.derivations.push(
          entry.error != null
            ? { keyName: key, error: entry.error }
            : {
                keyName: key,
                value: derivedScalar(
                  entry.value ?? null,
                  this.metadata.derivationDisplayTextOf(key),
                ),
              },
        )
        continue
      }

      if (entry.error != null) {
        reply.failures.set(key, entry.error)
        continue
      }
      const rows = rowsOf(entry.value)
      if (rows.length) reply.options.set(key, rows)
      else
        reply.failures.set(key, {
          message: 'no rows in the resolved value',
          value: entry.value,
        })
    }

    // A derivation the reply never mentioned is a failure of that one field, said as such.
    derivationKeys.forEach(key => {
      if (!answered.has(key)) {
        reply.derivations.push({
          keyName: key,
          error: { message: 'dialog-metadata/resolve answered nothing for it' },
        })
      }
    })
    return reply
  }
}

/**
 * The rows of one list result: `{options: [row…]}` (the PVAL-26675 projection), or a bare array (an
 * RPS that predates it). Anything that is not an object is dropped rather than rendered as
 * `[object Object]` or as a blank row.
 */
export function rowsOf(value: unknown): ListRecord[] {
  const rows = Array.isArray(value)
    ? value
    : (value as { options?: unknown } | null)?.options
  if (!Array.isArray(rows)) return []
  return rows.filter(
    (row): row is ListRecord =>
      row != null && typeof row === 'object' && !Array.isArray(row),
  )
}
